import React, { useState } from 'react';
import TaxonomySelect from './TaxonomySelect';

interface RenameDialogProps {
  // Header + button copy — varies between "Fork lineage" and "Edit cycle".
  title: string;
  introCopy: string;
  submitLabel: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  // When true, the submit button is disabled unless the user has changed
  // at least the title (used by Fork lineage so a "fork without rename"
  // can't slip through — backend also enforces).
  requireNameChange?: boolean;
  // Current values shown as starting state.
  currentName: string;
  currentType: string;
  currentAudience: string;
  // Walk fix 2026-08-12 (Fredrik) — aligned with the dashboard's Edit
  // Content Details modal: same registry-fed vocabulary (TaxonomySelect)
  // and, when showExtendedFields is on, the same full field set
  // (Public name + Format read-only + Channel filtered by format).
  showExtendedFields?: boolean;
  currentPublicName?: string;
  currentChannel?: string;
  // 14e walk station 4 — editable source, aligned with the dashboard's
  // Edit Content Details. Link + audited exception only (companion FILE
  // replacement is a resubmit concern).
  currentSourceLink?: string;
  currentNoSourceReason?: string;
  // v2 (D90) — slug-stored lineage dims (extended form only).
  currentTopic?: string;
  currentGeography?: string;
  format?: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  // Caller-supplied submit handler; receives only the fields the user
  // actually changed. Returns a promise — modal shows a busy state until
  // it resolves. If it throws, the message is shown inline.
  onSubmit: (changes: {
    newName?: string;
    newType?: string;
    newAudience?: string;
    newPublicName?: string;
    newChannel?: string;
    newSourceLink?: string;
    newNoSourceReason?: string;
    newTopic?: string;
    newGeography?: string;
    reason: string;
  }) => Promise<void>;
  onClose: () => void;
}

/**
 * Phase 8C remainder — shared dialog for cycle edit / fork-lineage flows.
 *
 * Pre-fills with the current contentItem values; submit only carries the
 * fields the user changed (so the backend audit trail records the actual
 * diff). Type / Audience / Channel are registry-fed via TaxonomySelect —
 * the SAME vocabulary as submission and the dashboard's Edit Content
 * Details modal (previously this dialog used the legacy hardcoded
 * PURPOSE_OPTIONS list, so the two editors offered different options —
 * Fredrik, UI walk 2026-08-12).
 */
const RenameDialog: React.FC<RenameDialogProps> = ({
  title,
  introCopy,
  submitLabel,
  reasonLabel,
  reasonPlaceholder,
  requireNameChange = false,
  currentName,
  currentType,
  currentAudience,
  showExtendedFields = false,
  currentPublicName = '',
  currentChannel = '',
  currentSourceLink = '',
  currentNoSourceReason = '',
  currentTopic = '',
  currentGeography = '',
  format = null,
  getAuthHeaders,
  onSubmit,
  onClose
}) => {
  const [name, setName] = useState(currentName);
  const [type, setType] = useState(currentType);
  const [audience, setAudience] = useState(currentAudience);
  const [publicName, setPublicName] = useState(currentPublicName);
  const [channel, setChannel] = useState(currentChannel);
  const [sourceLink, setSourceLink] = useState(currentSourceLink);
  const [noSourceReason, setNoSourceReason] = useState(currentNoSourceReason);
  // v2 (D90) — slug-stored lineage dims, editable in the extended form.
  const [topic, setTopic] = useState(currentTopic);
  const [geography, setGeography] = useState(currentGeography);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== currentName.trim();
  const typeChanged = type.trim() !== currentType.trim();
  const audienceChanged = audience.trim() !== currentAudience.trim();
  const publicNameChanged = showExtendedFields && publicName.trim() !== currentPublicName.trim();
  const channelChanged = showExtendedFields && channel.trim() !== currentChannel.trim();
  const sourceLinkChanged = showExtendedFields && sourceLink.trim() !== currentSourceLink.trim();
  const noSourceReasonChanged = showExtendedFields && noSourceReason !== currentNoSourceReason;
  const topicChanged = showExtendedFields && topic !== currentTopic;
  const geographyChanged = showExtendedFields && geography !== currentGeography;
  const anyChange = nameChanged || typeChanged || audienceChanged || publicNameChanged || channelChanged || sourceLinkChanged || noSourceReasonChanged || topicChanged || geographyChanged;

  const canSubmit = !submitting && trimmedName.length > 0 &&
    (requireNameChange ? nameChanged : anyChange);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const changes: Parameters<typeof onSubmit>[0] = { reason: reason.trim() };
      if (nameChanged) changes.newName = trimmedName;
      if (typeChanged) changes.newType = type.trim();
      if (audienceChanged) changes.newAudience = audience.trim();
      // Public name may legitimately be cleared — an empty string is a
      // real change (backend maps falsy → null).
      if (publicNameChanged) changes.newPublicName = publicName.trim();
      if (channelChanged) changes.newChannel = channel.trim();
      if (sourceLinkChanged) changes.newSourceLink = sourceLink.trim();
      if (noSourceReasonChanged) changes.newNoSourceReason = noSourceReason;
      if (topicChanged) changes.newTopic = topic;
      if (geographyChanged) changes.newGeography = geography;
      await onSubmit(changes);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#1a1a1a' }}>{title}</h3>
        <p style={{ fontSize: 13, color: '#555', marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
          {introCopy}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="review-form-group">
            <label>Title{requireNameChange ? <span style={{ color: '#dc3545' }}> *</span> : null}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter the new title"
              autoFocus
            />
            {requireNameChange && !nameChanged && (
              <div style={{ fontSize: 12, color: '#856404', marginTop: 4 }}>
                New title must differ from the current one to fork the lineage.
              </div>
            )}
          </div>

          {showExtendedFields && (
            <div className="review-form-group">
              <label>
                Public name{' '}
                <span style={{ fontSize: 11, color: '#6c757d', fontWeight: 'normal' }}>
                  (optional — filename shown to external users on HubSpot)
                </span>
              </label>
              <input
                value={publicName}
                onChange={e => setPublicName(e.target.value)}
                placeholder={trimmedName || 'Defaults to Title'}
              />
            </div>
          )}

          <div className="review-form-group">
            <label>Type</label>
            <TaxonomySelect
              kind="type"
              value={type}
              onChange={setType}
              getAuthHeaders={getAuthHeaders}
            />
          </div>

          {showExtendedFields && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="review-form-group">
                <label>Topic</label>
                <TaxonomySelect
                  kind="topic"
                  value={topic}
                  onChange={setTopic}
                  getAuthHeaders={getAuthHeaders}
                  placeholder="Select topic..."
                />
              </div>
              <div className="review-form-group">
                <label>Geography</label>
                <TaxonomySelect
                  kind="geography"
                  value={geography}
                  onChange={setGeography}
                  getAuthHeaders={getAuthHeaders}
                  placeholder="Global"
                />
              </div>
            </div>
          )}

          {showExtendedFields && (
            <>
              <div className="review-form-group">
                <label>Format</label>
                <div style={{ fontSize: 13, color: '#495057', padding: '6px 0' }}>
                  {format ? (
                    <>
                      <strong>{format.toUpperCase()}</strong>
                      <span style={{ fontSize: 11, color: '#6c757d', marginLeft: 8 }}>
                        detected from the file — determines which channels are available below
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: '#6c757d' }}>
                      unknown (legacy cycle) — channel list not filtered
                    </span>
                  )}
                </div>
              </div>
              <div className="review-form-group">
                <label>
                  Channel{' '}
                  <span style={{ fontSize: 11, color: '#6c757d', fontWeight: 'normal' }}>
                    (where this goes after approval)
                  </span>
                </label>
                <TaxonomySelect
                  kind="channel"
                  value={channel}
                  onChange={setChannel}
                  getAuthHeaders={getAuthHeaders}
                  filterFormat={format}
                />
              </div>
            </>
          )}

          <div className="review-form-group">
            <label>Target Audience</label>
            <TaxonomySelect
              kind="audience"
              value={audience}
              onChange={setAudience}
              getAuthHeaders={getAuthHeaders}
            />
          </div>

          {showExtendedFields && format !== 'webpage' && (() => {
            const isSourceRequired = format !== null && ['pdf', 'image', 'video'].includes(format);
            const hasReason = Boolean(noSourceReason);
            return (
              <div className="review-form-group" style={{ paddingTop: 10, borderTop: '1px solid #eee' }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  Editable source
                  {isSourceRequired && (
                    <span style={{ color: '#c73535', marginLeft: 4 }} title="Required — the editable original lives outside this file">*</span>
                  )}{' '}
                  <span style={{ fontSize: 11, color: '#6c757d', fontWeight: 'normal' }}>
                    (link to where the editable original lives)
                  </span>
                </label>
                <input
                  type="text"
                  value={sourceLink}
                  onChange={e => { setSourceLink(e.target.value); if (e.target.value) setNoSourceReason(''); }}
                  placeholder={format === 'video'
                    ? 'https://frame.io/… or vm:render-vm/projects/….aep'
                    : format === 'image'
                      ? 'https://figma.com/file/…'
                      : 'https://cellcoab.sharepoint.com/… or https://drive.google.com/…'}
                  disabled={hasReason}
                  style={{ opacity: hasReason ? 0.5 : 1 }}
                />
                {isSourceRequired && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    background: hasReason ? '#fff8ee' : '#fafafa',
                    border: `1px solid ${hasReason ? '#f0d9b5' : '#eee'}`,
                    borderRadius: 8,
                    fontSize: 12
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', color: '#444', fontWeight: 500 }}>
                      <input
                        type="checkbox"
                        checked={hasReason}
                        onChange={e => {
                          if (e.target.checked) { setNoSourceReason(noSourceReason || 'legacy'); setSourceLink(''); }
                          else setNoSourceReason('');
                        }}
                        style={{ width: 14, height: 14, margin: 0, flexShrink: 0 }}
                      />
                      No editable source available
                      <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(audited exception)</span>
                    </label>
                    {hasReason && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingLeft: 22 }}>
                        <span style={{ fontSize: 11, color: '#8a6d3b', flexShrink: 0 }}>Reason</span>
                        <select
                          value={noSourceReason}
                          onChange={e => setNoSourceReason(e.target.value)}
                          style={{ flex: '0 1 240px', padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}
                        >
                          <option value="legacy">Legacy content (predates working-files policy)</option>
                          <option value="scanned">Scanned / OCR only</option>
                          <option value="third-party">Third-party file (no source available)</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="review-form-group">
            <label>{reasonLabel}</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
            />
          </div>

          {error && (
            <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12, background: '#f8d7da', padding: '8px 12px', borderRadius: 4 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              className="review-btn review-btn-outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="review-btn review-btn-primary"
              disabled={!canSubmit}
            >
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RenameDialog;
